import { FallbackError } from "@core/domain/errors/fallback.error";
import { ReportDatasource } from "@report/data/datasources/report.datasource";
import type { ReportRouteGpsFilter } from "@report/domain/models/report-route-gps.ts";
import { ReportRouteGps } from "@report/domain/models/report-route-gps.ts";
import type {
    ReportHistoricalCustomFilter,
    ReportHistoricalFilter,
} from "@report/domain/models/report.model";
import { Report } from "@report/domain/models/report.model";
import type { ReportRepository } from "@report/domain/repositories/report.repository";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";

@injectable()
export class ReportImplRepository implements ReportRepository {
    constructor(
        @inject(ReportDatasource)
        private readonly reportDatasource: ReportDatasource,
    ) {}

    fetchHistoricalReport(
        historicalFilters: ReportHistoricalFilter,
    ): ResultAsync<Report[], FallbackError> {
        return this.reportDatasource.fetchHistoricalReport(historicalFilters);
    }

    fetchHistoricalCustomReport(
        historicalCustomFilters: ReportHistoricalCustomFilter,
    ): ResultAsync<Report[], FallbackError> {
        return this.reportDatasource.fetchHistoricalCustomReport(
            historicalCustomFilters,
        );
    }

    requestHistoricalReportMail(
        historicalFilters: ReportHistoricalFilter,
    ): ResultAsync<boolean, FallbackError> {
        return this.reportDatasource.requestMailHistoricalReport(
            historicalFilters,
        );
    }

    fetchReportRouteGps(
        reportRouteGpsFilter: ReportRouteGpsFilter,
    ): ResultAsync<ReportRouteGps[], FallbackError> {
        return this.reportDatasource.fetchRouteGps(reportRouteGpsFilter);
    }

    fetchDailyActivityReport(
        reportHistoricalFilter: ReportHistoricalFilter,
    ): ResultAsync<Report[], FallbackError> {
        return this.reportDatasource.fetchDailyActivityReport(
            reportHistoricalFilter,
        );
    }

    fetchDetailedStateReport(
        reportHistoricalFilter: ReportHistoricalFilter,
    ): ResultAsync<Report[], FallbackError> {
        return this.reportDatasource.fetchDetailedStateReport(
            reportHistoricalFilter,
        );
    }

    fetchPoisRoutesReport(
        reportHistoricalFilter: ReportHistoricalFilter,
    ): ResultAsync<Report[], FallbackError> {
        return this.reportDatasource.fetchPoisRoutesReport(
            reportHistoricalFilter,
        );
    }

    fetchPoisVisitedReport(
        reportHistoricalFilter: ReportHistoricalFilter,
    ): ResultAsync<Report[], FallbackError> {
        return this.reportDatasource.fetchPoisVisitedReport(
            reportHistoricalFilter,
        );
    }

    fetchStateGpsReport(
        reportHistoricalFilter: ReportHistoricalFilter,
    ): ResultAsync<Report[], FallbackError> {
        return this.reportDatasource.fetchStateGpsReport(
            reportHistoricalFilter,
        );
    }

    fetchTraveledKilometersReport(
        reportHistoricalFilter: ReportHistoricalFilter,
    ): ResultAsync<Report[], FallbackError> {
        return this.reportDatasource.fetchTraveledKilometersReport(
            reportHistoricalFilter,
        );
    }
}
